const waitForPoll = (milliseconds, signal) =>
    new Promise((resolve) => {
        if (signal.aborted) return resolve()
        const finish = () => {
            clearTimeout(timer)
            signal.removeEventListener('abort', finish)
            resolve()
        }
        const timer = setTimeout(finish, milliseconds)
        signal.addEventListener('abort', finish, { once: true })
    })

// A result connection ending is not evidence that the vendor work ended.
// Recovery reads the existing run and its append-only snapshot; it never starts a run.
export async function followLiveSearch({
    reader,
    signal,
    fetchStatus,
    onRows,
    onState,
    flowId: initialFlowId = null,
    offset: initialOffset = 0,
}) {
    let flowId = initialFlowId
    let progress = null
    const resumed = Boolean(initialFlowId)
    let explicitComplete = false
    const publish = (state) => {
        if (!signal.aborted) onState({ flowId, ...state })
    }
    const cancelReader = () => {
        if (reader) Promise.resolve(reader.cancel()).catch(() => {})
    }
    const consume = (line) => {
        if (!line.startsWith('data:')) return
        const event = JSON.parse(line.slice(5).trim())
        if (Array.isArray(event)) onRows(event)
        else if (event.flow_id) {
            flowId = event.flow_id
            publish({})
        } else if (['progress', 'complete'].includes(event.event)) {
            progress = event
            publish({
                vendors: event.vendors || [],
                delivery: 'streaming',
            })
            if (event.event === 'complete') explicitComplete = true
        }
    }

    if (reader) {
        publish({ delivery: 'streaming' })
        signal.addEventListener('abort', cancelReader, { once: true })
        try {
            const decoder = new TextDecoder()
            let buffer = ''
            while (!signal.aborted && !explicitComplete) {
                const { done, value } = await reader.read()
                if (signal.aborted) return
                buffer += done ? decoder.decode() : decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop()
                for (const line of lines) consume(line)
                if (done) {
                    if (buffer.trim()) consume(buffer)
                    break
                }
            }
        } catch {
            // Recover after network, framing, or reader errors using the saved run.
        } finally {
            signal.removeEventListener('abort', cancelReader)
            cancelReader()
        }
    }
    if (signal.aborted) return
    if (explicitComplete && progress?.status === 'complete' && progress?.vendors?.length) {
        publish({
            complete: true,
            delivery: 'complete',
        })
        return
    }
    // If submission failed before identifying a run, do not guess an older run
    // under the same SearchAction or silently submit a duplicate search.
    if (!flowId) {
        publish({
            stopped: true,
            delivery: 'stopped',
        })
        return
    }

    publish({
        delivery: resumed ? 'checking' : 'recovering',
    })
    const stop = () => publish({ stopped: true, delivery: 'stopped' })
    let offset = initialOffset
    let failureStartedAt = null
    while (!signal.aborted) {
        const attemptStartedAt = Date.now()
        if (failureStartedAt !== null && attemptStartedAt - failureStartedAt >= 60000) {
            stop()
            return
        }
        try {
            const snapshot = await fetchStatus(flowId, offset, signal)
            if (signal.aborted) return
            // Missing or invalid progress is not evidence of an active search.
            if (
                !Array.isArray(snapshot?.results) ||
                !['running', 'complete', 'unknown'].includes(snapshot.status) ||
                snapshot.next_offset !== offset + snapshot.results.length ||
                typeof snapshot.has_more !== 'boolean' ||
                (snapshot.has_more && snapshot.next_offset <= offset)
            ) {
                stop()
                return
            }
            onRows(snapshot.results)
            publish({
                vendors: snapshot.vendors || [],
                // Only show resumed work as searching after the backend confirms it.
                delivery: snapshot.has_more
                    ? 'loading'
                    : snapshot.status === 'running'
                      ? resumed
                          ? 'polling'
                          : 'recovering'
                      : 'checking',
            })
            failureStartedAt = null
            offset = snapshot.next_offset
            // Drain saved pages before stopping, including when monitoring gave up.
            if (snapshot.has_more) continue
            if (snapshot.status === 'unknown') {
                stop()
                return
            }
            if (snapshot.status === 'complete') {
                publish({
                    complete: true,
                    delivery: 'complete',
                })
                return
            }
        } catch (error) {
            if (signal.aborted) return
            publish({
                delivery: 'recovering',
            })
            // Retry temporary connection failures for about a minute; permanent
            // request failures and prolonged outages stop recovery, not the run.
            failureStartedAt ??= attemptStartedAt
            const status = error?.response?.status
            if (
                (status >= 400 && status < 500 && ![408, 429].includes(status)) ||
                Date.now() - failureStartedAt >= 60000
            ) {
                stop()
                return
            }
        }
        await waitForPoll(10000, signal)
    }
}
